import { CheckCircleIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  BoxProps,
  Button,
  ButtonGroup,
  Center,
  CircularProgress,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Input,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Skeleton,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useUserContext } from '@providers/useUserContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import FloatingLabelInput from 'components/FloatingLabelInput';
import {
  useCrossRegionalAssociations,
  useFetchClientCompleteInfo,
  useFetchMe,
  useListClientServices,
  useListCoaches,
  useListLocations,
  useListPurchases,
  useListSites,
  useListVisits,
  useZingfitVisits,
} from 'hooks/useApiQueries';
import { produce } from 'immer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactInputMask from 'react-input-mask';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ClientService, MindbodyUser } from 'types/client';
import type { ProfileCopy, ReferralPublicConfig } from 'types/referral';
import { resolveProfileCopy } from 'utils/referralProfileCopy';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP, useCopyToClipboard } from 'utils/http';
import { parseMindbodyError } from 'utils/mindbodyError';
import { resolveUserError } from 'utils/userErrorMessages';
import ServicesList from './Services/ServicesList';
import { MySubscription } from './Subscription/MySubscription';
import VisitsList from './Visits/VisitsList';
import { getBrandColor } from 'utils/brandColor';

const primaryColor = getBrandColor();

const MotionBox = motion(Box);

function UserProfile() {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        // Ajouter un délai avant de faire défiler
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500); // 500 ms de délai
    }
  }, [location]);

  return (
    <SimpleGrid className="myAccountBlocksContainer" columns={{ base: 1, md: 2, lg: 2, xl: 2 }} spacing={5} pb={5}>
      <Sessions />
      <Credits />
      <MySubscription />
      <Statistics />
      <Profile />
      <EditPassword />
      <PaymentMethod />
      <Purchases />
      <Sponsorsip />
    </SimpleGrid>
  );
}

function Sessions() {
  const navigate = useNavigate();
  const { data: visits, isLoading } = useListVisits() || {};
  const { data: sites } = useListSites();
  const { data: crossRegionalAssociationsData } = useCrossRegionalAssociations();
  const [selectedSiteId, setSelectedSiteId] = useState<string | number | null>(null);

  // Filtrer les sites pour ne garder que ceux qui existent dans crossRegionalAssociations
  const filteredSites = useMemo(() => {
    const crossRegionalSiteIds = new Set(
      crossRegionalAssociationsData?.CrossRegionalClientAssociations.map((assoc) => assoc.SiteId),
    );
    return sites?.filter((site) => crossRegionalSiteIds.has(site.Id));
  }, [sites, crossRegionalAssociationsData]);

  const futureVisits = useMemo(() => {
    if (!visits) return [];
    return visits
      ?.filter((visit) => (selectedSiteId ? visit.SiteId === Number(selectedSiteId) : true))
      ?.filter((visit) => visit.VisitType !== -1)
      ?.filter((visit) => new Date(visit.StartDateTime).getTime() > new Date().getTime())
      .sort((a, b) => new Date(a.StartDateTime).getTime() - new Date(b.StartDateTime).getTime());
  }, [visits, selectedSiteId]);

  const totalVisits = useMemo(() => visits?.length ?? 0, [visits]);

  const handleSiteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSiteId(event.target.value);
  };

  return (
    <Box className="accountBox" w="full" sx={{}}>
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES PROCHAINES SESSIONS MONDAY
      </Heading>
      <VStack w="full" alignItems={'flex-start'} sx={{ maxWidth: '250px', paddingLeft: '1em' }}>
        <Select placeholder="Filtrer par marque" sx={{ maxWidth: '250px' }} onChange={handleSiteChange}>
          {filteredSites?.map((site) => (
            <option key={site.Id} value={site.Id}>
              {site.Name}
            </option>
          ))}
        </Select>
      </VStack>
      <Skeleton isLoaded={!isLoading}>
        <VStack w="full">
          <VisitsList visits={futureVisits} />
        </VStack>
        {/* Bouton pour naviguer vers l'historique des visites */}
        <VStack padding="2em 1em">
          {futureVisits.length === 0 && (
            <>
              <Text align="center">
                Vous n'avez pas encore réservé de cours {selectedSiteId ? 'au sein des studios de cette marque' : ''}
              </Text>
              {!selectedSiteId && (
                <Button className="accountButton" variant="contained" onClick={() => navigate('/')}>
                  Réserver une session
                </Button>
              )}
            </>
          )}
          {totalVisits > 0 && (
            <Button
              sx={{ marginTop: '12.5em' }}
              className="accountButton"
              variant="contained"
              onClick={() => navigate('visits')}
            >
              Voir toutes mes sessions
            </Button>
          )}
        </VStack>
      </Skeleton>
    </Box>
  );
}

function Credits() {
  const { data: services, isLoading } = useListClientServices();
  const servicesArray = services ? Object.values(services) : [];
  const isMobile = useBreakpointValue({ base: true, md: false }) || false;

  const navigate = useNavigate();

  const validServices =
    servicesArray
      ?.filter((service: ClientService) => {
        const expirationDate = service.ExpirationDate;
        // Vérifie que 'expirationDate' n'est pas 'null' avant de comparer.
        // Utiliser >= pour inclure les crédits qui expirent aujourd'hui
        return expirationDate !== null && new Date(expirationDate) >= new Date() && service.Remaining > 0;
      })
      .sort(
        (a: ClientService, b: ClientService) => new Date(b.ActiveDate).getTime() - new Date(a.ActiveDate).getTime(),
      ) || // Trier par date d'activation la plus récente
    [];

  return (
    <Box className={!isMobile ? 'SkeletonServices accountBox' : 'accountBox'} w="full">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES CRÉDITS MONDAY
      </Heading>
      <Skeleton isLoaded={!isLoading}>
        <VStack w="full">
          <ServicesList services={validServices} />z
        </VStack>
        {/* Bouton pour naviguer vers l'historique des crédits */}
        <VStack padding="2em 1em">
          {validServices.length === 0 ? (
            <>
              <Text>Vous n'avez pas de crédit</Text>
              <Button variant="contained" onClick={() => navigate('/tarifs')} className="accountButton">
                Voir nos tarifs
              </Button>
            </>
          ) : (
            <HStack flexDirection={isMobile ? 'column' : 'row'}>
              <Button
                width={isMobile ? '228px' : 'auto'}
                className="accountButton"
                variant="contained"
                onClick={() => navigate('services')}
              >
                Voir tous mes crédits
              </Button>
              <Button
                width={isMobile ? '228px' : 'auto'}
                className="accountButton"
                variant="contained"
                onClick={() => navigate('/tarifs')}
              >
                Acheter des crédits
              </Button>
            </HStack>
          )}
        </VStack>
      </Skeleton>
    </Box>
  );
}

function Statistics() {
  const { data: visits } = useListVisits() || {};
  const { data: sites } = useListSites();
  // Historique Zingfit (pré-migration, avant avril 2024) par site, fourni par
  // le back (Paris uniquement). Un site absent = pas de donnée ZF fiable.
  const { data: zingfitVisits } = useZingfitVisits() || {};
  // Le compteur "milestones" ne doit refléter que les sessions réellement
  // réalisées : on exclut les late cancels (LateCancelled), les no-show
  // (Missed), les réservations à venir (StartDateTime dans le futur), les
  // visites hors région (crossRegionalLookup remonte les visites de toutes
  // les villes ; la fidélité Monday Collective ne compte que la région du
  // site) et les "Cross Region Visit" (VisitType === -1). Ces dernières sont
  // des doublons que Mindbody crée sur le site dont le forfait a payé la
  // séance : la vraie visite existe déjà au même créneau sur un autre site de
  // la région, donc les compter gonfle le total (même convention que
  // VisitsList / VisitsHistory). `regionalSiteIds` est injecté par
  // Frontend.php — en son absence (bundle servi par un back pas encore à
  // jour), on ne filtre pas par site.
  const regionalSiteIds = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.regionalSiteIds : undefined;
  const countedVisits = useMemo(() => {
    const now = Date.now();
    return visits?.filter(
      (visit) =>
        !visit.LateCancelled &&
        !visit.Missed &&
        visit.VisitType !== -1 &&
        new Date(visit.StartDateTime).getTime() <= now &&
        (!regionalSiteIds?.length || regionalSiteIds.includes(visit.SiteId)),
    );
  }, [visits, regionalSiteIds]);

  const visitsBySite = useMemo(() => {
    const counts: { [key: string]: number } = {};
    // Visites "Converted Visit" par site : c'est l'import Zingfit de Mindbody,
    // comptabilisé à part car il entre en concurrence avec le décompte Zingfit
    // de Stretch (voir plus bas).
    const convertedBySite: { [key: string]: number } = {};

    // Dédoublonner les seules "Converted Visit" (import Zingfit de Mindbody) :
    // l'import a créé le même créneau jusqu'à 6 fois sur certains comptes, ce
    // sont de purs artefacts (ClassId 0, aucun forfait débité) et les laisser
    // passer fausserait aussi le max(Mindbody, Stretch) plus bas.
    // Les réservations multiples sur un vrai cours, elles, comptent chacune :
    // ce sont des places offertes à des invités, avec un Id de visite propre et
    // un forfait débité à chaque fois (décision produit, Daphné 01/09/2026).
    const seenConverted = new Set<string>();
    const uniqueVisits = countedVisits?.filter((visit) => {
      if (visit.Name !== 'Converted Visit') {
        return true;
      }
      const key = `${visit.SiteId}|${visit.ClientId}|${visit.StartDateTime}|${visit.EndDateTime}`;
      if (seenConverted.has(key)) {
        return false;
      }
      seenConverted.add(key);
      return true;
    });

    uniqueVisits?.forEach((visit) => {
      const siteId = String(visit.SiteId);
      if (visit.Name === 'Converted Visit') {
        convertedBySite[siteId] = (convertedBySite[siteId] || 0) + 1;
      } else {
        counts[siteId] = (counts[siteId] || 0) + 1;
      }
    });

    // Historique Zingfit (pré-migration, Paris) : on dispose de deux sources
    // partielles et aucune n'est fiable seule.
    //  - Mindbody, via les "Converted Visit" : import inégal d'un client à
    //    l'autre, parfois vide.
    //  - Stretch : reconstruit depuis le fichier Zingfit, indexé sur l'adresse
    //    mail principale du client dans Mindbody. Un client qui avait une autre
    //    adresse à l'époque Zingfit s'y retrouve sous-compté (cas vu en prod :
    //    136 séances côté Mindbody, 4 seulement côté Stretch).
    // On garde donc le plus complet des deux plutôt que de substituer l'un à
    // l'autre. Sans donnée Stretch pour le site (hors Paris, ou récupération en
    // échec), on retombe sur le décompte Mindbody seul.
    const siteIdsWithHistory = new Set([...Object.keys(convertedBySite), ...Object.keys(zingfitVisits ?? {})]);
    siteIdsWithHistory.forEach((siteId) => {
      if (regionalSiteIds?.length && !regionalSiteIds.includes(Number(siteId))) {
        return;
      }
      const fromMindbody = convertedBySite[siteId] || 0;
      const fromStretch = zingfitVisits?.[siteId];
      counts[siteId] = (counts[siteId] || 0) + (fromStretch === undefined ? fromMindbody : Math.max(fromMindbody, fromStretch));
    });

    return counts;
  }, [countedVisits, zingfitVisits, regionalSiteIds]);

  // Total = somme des compteurs par site (Mindbody réalisé + Zingfit), pour
  // garantir la cohérence entre le grand chiffre et le détail par marque.
  const totalVists = useMemo(
    () => Object.values(visitsBySite).reduce((sum, count) => sum + count, 0),
    [visitsBySite],
  );

  return (
    <Box className="accountBox" w="full" sx={{}}>
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MES STATS MONDAY
      </Heading>
      <Box w="full" sx={{ padding: '1em' }}>
        <Box
          width="100%"
          flexDirection="column"
          alignContent="flex-start"
          justifyContent="flex-start"
          className="accountBigStat"
        >
          <Text fontSize="6xl" fontWeight="bold" my={4} textAlign="left" margin={0} className="accountBigStatNumber">
            {totalVists}
          </Text>
          <Text fontSize="2xl" textAlign="left" className="accountBigStatLabel">
            COURS RÉALISÉS CHEZ MONDAY
          </Text>
        </Box>
        {Object.entries(visitsBySite || {}).map(([siteId, visitCount]) => {
          const site = sites ? sites.find((site) => site.Id === parseInt(siteId)) : null;
          return (
            <Box key={siteId} mt={4}>
              <HStack width="100%" justifyContent="space-between">
                <Text textAlign="left" fontWeight="bold">
                  {visitCount}
                </Text>
                <Text textTransform="uppercase">cours réalisés chez {site ? site.Name : '...'}</Text>
              </HStack>
            </Box>
          );
        })}
        <Divider mt={5} />
        <Text textTransform="uppercase" fontSize="xl" mt={4} fontWeight="semibold">
          Mon studio préféré
        </Text>

        <StatisticsPreferredStudio />
        <StatisticsPreferredCoach />
      </Box>
    </Box>
  );
}

function Profile() {
  const { data: user, isLoading } = useFetchMe();

  const navigate = useNavigate();
  const ShoeSize = user?.CustomClientFields.find((customField) => customField.Name === 'Shoe size')?.Value as number;
  const handleEditClick = () => navigate('edit');

  return (
    <Box className="accountBox" w="full">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        MON PROFIL
      </Heading>
      <Skeleton isLoaded={!isLoading}>
        <Box w="full" sx={{ padding: '1em' }}>
          <HStack width="100%" justifyContent="space-between">
            <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
              Nom
            </Text>
            <Text align="right">
              {user?.FirstName} {user?.LastName}
            </Text>
          </HStack>
          {ShoeSize && (
            <HStack width="100%" justifyContent="space-between">
              <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
                Pointure
              </Text>
              <Text align="right">{ShoeSize}</Text>
            </HStack>
          )}
          <HStack width="100%" justifyContent="space-between" alignItems="flex-start" mt={4}>
            <Text align="left" fontWeight="medium" textTransform="uppercase" className="accountFormLabel">
              Adresse
            </Text>
            <Box textAlign="right">
              <Text>{user?.AddressLine1}</Text>
              <Text>{user?.AddressLine2}</Text>
              <Text>
                {user?.PostalCode} {user?.City}
              </Text>
            </Box>
          </HStack>
          <HStack width="100%" justifyContent="space-between" mt={4}>
            <Text align="left" textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
              Téléphone
            </Text>
            <Text align="right">{user?.MobilePhone}</Text>
          </HStack>
          <HStack width="100%" justifyContent="space-between" mt={4}>
            <Text align="left" textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
              Email
            </Text>
            <Text align="right">{user?.Email}</Text>
          </HStack>
        </Box>
        <VStack padding="2em 1em">
          <Button variant="contained" mt={4} onClick={handleEditClick} className="accountButton">
            MODIFIER
          </Button>
        </VStack>
      </Skeleton>
    </Box>
  );
}

/**
 * Config publique du parrainage : `campaigns` ne contient que les campagnes
 * utilisables (active + dans la fenêtre + quota global non atteint), et
 * `defaultCampaignId` est la campagne cochée « Par défaut » dans le BO.
 *
 * Deux consommateurs sur cette page (le lien du parrain et les textes de son
 * encart) : même clé de cache, donc une seule requête.
 */
const useReferralPublicConfig = () =>
  useQuery({
    queryKey: ['/referral/active-campaigns'],
    queryFn: () => HTTP.get<ReferralPublicConfig>('/referral/active-campaigns').send(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }).data;

/**
 * Textes de l'encart parrainage. La résolution (et son repli sur les textes
 * d'origine) vit dans `utils/referralProfileCopy`, où elle est testée.
 */
const useProfileCopy = (): ProfileCopy => {
  const referralConfig = useReferralPublicConfig();
  return useMemo(() => resolveProfileCopy(referralConfig), [referralConfig]);
};

const useReferralLink = () => {
  const { user } = useUserContext();
  const { pathname } = useLocation();
  const referralConfig = useReferralPublicConfig();

  return useMemo(() => {
    const { UniqueId, Id } = user!;
    const baseUrl = window.location.href.split(pathname).shift() ?? '';

    if (!baseUrl) return null;

    // Une campagne par défaut est réglée et toujours utilisable : le lien du
    // profil porte la campagne, exactement comme le lien de publipostage
    // Stretch (`?campaign=&clientId=`), pour que le filleul soit attribué à
    // cette campagne et non à la récompense historique.
    const defaultCampaignId = referralConfig?.defaultCampaignId ?? null;
    const isDefaultUsable =
      !!defaultCampaignId && !!referralConfig?.campaigns?.some((c) => c.id === defaultCampaignId);

    if (isDefaultUsable) {
      const campaign = encodeURIComponent(defaultCampaignId);
      const clientId = encodeURIComponent(String(Id));
      return `${baseUrl}/account?campaign=${campaign}&clientId=${clientId}`;
    }

    // Aucune campagne par défaut (ou campagne expirée / épuisée) : format
    // historique. Le serveur retombe alors sur le fallback legacy, le parrain
    // garde donc sa récompense dans tous les cas.
    const encodedData = btoa(JSON.stringify({ UniqueId, Id }));
    return `${baseUrl}/account?referral=${encodedData}`;
  }, [user, pathname, referralConfig]);
};

function Sponsorsip() {
  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();
  const referralLink = useReferralLink();
  const profileCopy = useProfileCopy();

  const affiliateInputRef = useRef<HTMLInputElement>(null);

  return (
    <Box className="accountBox" w="full" id="parrainage">
      <Heading className="accountSubTitle" size="md" sx={{ padding: '1em' }}>
        {t('PARRAINAGE')}
      </Heading>
      <Stack w="full" sx={{ padding: '1em' }}>
        <HStack width="100%">
          <Text align="left" fontWeight="medium" textTransform="uppercase">
            {profileCopy.heading}
          </Text>
        </HStack>

        <Text align="left" fontWeight={'medium'}>
          {profileCopy.subtitle}
        </Text>
        <Text mb={0} align="left">
          {profileCopy.stepsIntro}
        </Text>
        <List mt={0} spacing={3} paddingLeft={4}>
          {profileCopy.steps
            .filter((step) => step !== '')
            .map((step, i) => (
              <ListItem key={`${i}-${step}`} mt={i === 0 ? 0 : undefined}>
                <ListIcon as={CheckCircleIcon} color="green.500" />
                {step}
              </ListItem>
            ))}
        </List>
        <VStack mt={5}>
          <HStack alignItems="flex-end" w="full">
            <FormControl>
              <FormLabel>{t('Votre lien de parrainage')}</FormLabel>
              <Input ref={affiliateInputRef} readOnly value={referralLink ?? ''} />
            </FormControl>
            <Button onClick={() => copyToClipboard(affiliateInputRef.current!, t('Lien copié'))}>
              {t('Copier le lien')}
            </Button>
          </HStack>
          <ReferralInviteModal />
        </VStack>
      </Stack>
    </Box>
  );
}

interface ReferralFormData {
  email: string;
  message: string;
}

/** Doit rester aligné sur Client::REFERRAL_MESSAGE_MAX_LENGTH côté serveur. */
const REFERRAL_MESSAGE_MAX_LENGTH = 500;

function ReferralInviteModal() {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { register, handleSubmit, setError, formState, reset, watch } = useForm<ReferralFormData>({
    defaultValues: { email: '', message: '' },
  });
  const referralLink = useReferralLink();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const message = watch('message') ?? '';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  function close() {
    onClose();
    setSentTo(null);
    reset();
  }

  function inviteAnother() {
    setSentTo(null);
    reset();
  }

  function onSubmit(data: ReferralFormData) {
    return HTTP.post('/client/sendreferralemail', {
      email: data.email.trim(),
      message: data.message.trim(),
      link: referralLink,
    })
      .send()
      .then(() => setSentTo(data.email.trim()))
      .catch((error: ApiClientException) => {
        setError('root', { message: error.getMessage() });
      });
  }

  function shareLink() {
    if (!referralLink || !canShare) return;
    navigator
      .share({ title: t('Inviter vos proches'), url: referralLink })
      // L'utilisateur qui ferme la feuille de partage n'est pas une erreur.
      .catch(() => undefined);
  }

  return (
    <>
      <Button variant="contained" mt={4} onClick={onOpen} className="accountButton">
        {t('Inviter vos proches')}
      </Button>
      <Modal isOpen={isOpen} onClose={close} isCentered>
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(onSubmit)} mx={4}>
          <ModalHeader pr={12}>
            {t('Inviter vos proches')}
            <IconButton
              aria-label={t('Fermer')}
              icon={<CloseIcon />}
              onClick={close}
              position="absolute"
              top="8px"
              right="12px"
              size="sm"
            />
          </ModalHeader>

          {sentTo ? (
            <>
              <ModalBody>
                <VStack spacing={3} py={4} textAlign="center">
                  <CheckCircleIcon boxSize={12} color="green.500" />
                  <Heading as="p" size="md" mt={0}>
                    {t('Invitation envoyée')}
                  </Heading>
                  <Text mt={0}>
                    <Text as="strong">{sentTo}</Text> {t('va recevoir un email avec votre lien de parrainage')}
                  </Text>
                  <Text mt={0} fontSize="sm" color="gray.500">
                    {t("S'il vous répond, sa réponse arrivera directement dans votre boîte mail")}
                  </Text>
                </VStack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button variant="outline" onClick={inviteAnother}>
                    {t("Inviter quelqu'un d'autre")}
                  </Button>
                  <Button onClick={close}>{t('Terminer')}</Button>
                </ButtonGroup>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalBody>
                <Stack spacing={4}>
                  <Text mt={0} color="gray.600">
                    {t('Votre proche reçoit un email avec votre lien de parrainage pour créer son compte')}
                  </Text>
                  <FormControl isRequired>
                    <FormLabel>{t('Son adresse email')}</FormLabel>
                    <Input
                      {...register('email', { required: true })}
                      type="email"
                      autoComplete="off"
                      placeholder="prenom@exemple.com"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>{t('Un petit mot (facultatif)')}</FormLabel>
                    <Textarea
                      {...register('message', { maxLength: REFERRAL_MESSAGE_MAX_LENGTH })}
                      maxLength={REFERRAL_MESSAGE_MAX_LENGTH}
                      rows={3}
                      resize="vertical"
                      placeholder={t('Viens tester un cours avec moi !')}
                    />
                    <Text mt={1} fontSize="xs" color="gray.500" textAlign="right">
                      {message.length}/{REFERRAL_MESSAGE_MAX_LENGTH}
                    </Text>
                  </FormControl>

                  <FormControl isInvalid={!!formState.errors.root}>
                    <FormErrorMessage mt={0}>{formState.errors.root?.message}</FormErrorMessage>
                  </FormControl>
                </Stack>
              </ModalBody>

              <ModalFooter justifyContent={canShare ? 'space-between' : 'flex-end'}>
                {canShare && (
                  <Button variant="ghost" onClick={shareLink} isDisabled={!referralLink}>
                    {t('Partager le lien')}
                  </Button>
                )}
                <Button type="submit" isLoading={formState.isSubmitting}>
                  {t("Envoyer l'invitation")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

function StatisticsPreferredStudio() {
  const navigate = useNavigate();
  const { data: visits } = useListVisits() || {};
  const { data: studios } = useListLocations();

  // Filtrer les visites par le siteId actuel
  const filteredVisits = useMemo(
    () => visits?.filter((visit) => String(visit.SiteId) === mondayMindbody.siteId),
    [visits],
  );

  const visitsByLocation = useMemo(() => {
    const counts: { [key: string]: number } = {};

    filteredVisits?.forEach((visit) => {
      const locationId = `${visit.LocationId}+${visit.SiteId}`;
      counts[locationId] = (counts[locationId] || 0) + 1;
    });

    return counts;
  }, [filteredVisits]);

  const sortedLocationsByVisitsCount = useMemo(
    () => Object.entries(visitsByLocation || {}).sort((a, b) => b[1] - a[1]),
    [visitsByLocation],
  );
  const mostVisitCount = useMemo(
    () => sortedLocationsByVisitsCount && sortedLocationsByVisitsCount?.[0]?.[1],
    [sortedLocationsByVisitsCount],
  );
  const preferredLocation = useMemo(() => {
    if (sortedLocationsByVisitsCount.length) {
      // Prendre le premier élément du tableau trié, qui est le studio le plus fréquenté
      const [preferredLocationId] = sortedLocationsByVisitsCount?.[0] || [];
      if (preferredLocationId) {
        const [locationId, siteId] = preferredLocationId.split('+');
        // Si un studio préféré existe, afficher ses informations

        const preferredLocation = studios?.find(
          (studio) => String(studio.Id) === locationId && String(studio.SiteID) === siteId,
        );

        if (preferredLocation) {
          return preferredLocation;
        }
      }
    }
  }, [studios, sortedLocationsByVisitsCount]);

  if (preferredLocation) {
    return (
      <HStack key={preferredLocation.Id} width="100%" justifyContent="space-between">
        <Text
          color={primaryColor}
          textTransform="uppercase"
          fontSize="xl"
          mt={0}
          fontWeight="semibold"
          textAlign="left"
        >
          {preferredLocation.Name} ({mostVisitCount})
        </Text>
        <Button
          size="sm"
          textAlign="center"
          minWidth="135px"
          padding="2em 1em"
          variant="contained"
          onClick={() => navigate(`/studio/${preferredLocation.Id}`)}
          className="accountButton"
        >
          voir
          <br />
          le planning
        </Button>
      </HStack>
    );
  }
  // Sinon, on peut retourner null ou un message indiquant qu'aucun studio préféré n'a été trouvé.
  return <Text>Aucun studio préféré trouvé.</Text>;
}

function StatisticsPreferredCoach() {
  const navigate = useNavigate();
  const { data: visits } = useListVisits() || {};
  const { data: coaches } = useListCoaches();

  // Filtrer les visites et entraîneurs par le siteId en cours
  const filteredVisits = useMemo(() => {
    return visits?.filter((visit) => visit.SiteId.toString() === mondayMindbody.siteId);
  }, [visits]);

  const visitsByCoach = useMemo(() => {
    return filteredVisits?.reduce((acc, visit) => {
      const staffId = `${visit?.StaffId?.toString()}+${visit?.SiteId?.toString()}`;
      acc[staffId] = (acc[staffId] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });
  }, [filteredVisits]);

  const mostSeenCoachId = useMemo(() => {
    return Object.entries(visitsByCoach || {}).reduce<{
      id: string | null;
      count: number;
    }>(
      (maxCoach, [coachId, count]) => {
        return count > maxCoach.count ? { id: coachId, count } : maxCoach;
      },
      { id: null, count: 0 },
    ).id;
  }, [visitsByCoach]);

  const mostSeenCoach = useMemo(() => {
    if (mostSeenCoachId) {
      const [coachId, siteId] = mostSeenCoachId.split('+');
      const coach = coaches?.find((coach) => coach.Id.toString() === coachId && String(coach.SiteId) === siteId);
      return coach;
    }
  }, [coaches, mostSeenCoachId]);

  if (!mostSeenCoach) return null;

  return (
    <>
      <Text textTransform="uppercase" fontSize="xl" mt={4} fontWeight="semibold">
        MON COACH PRÉFÉRÉ
      </Text>
      <HStack width="100%" justifyContent="space-between">
        <Text
          color={primaryColor}
          textTransform="uppercase"
          fontSize="xl"
          mt={0}
          fontWeight="semibold"
          textAlign="left"
        >
          {mostSeenCoach.FirstName}
        </Text>
        <Button
          size="sm"
          textAlign="center"
          minWidth="135px"
          padding="2em 1em"
          variant="contained"
          onClick={() => navigate(`/coach/${mostSeenCoach.Id}`)}
          className="accountButton"
        >
          voir
          <br />
          son planning
        </Button>
      </HStack>
    </>
  );
}

function Purchases() {
  const { data, isLoading } = useListPurchases();
  const filteredPurchases = data?.filter(
    (purchase) =>
      purchase.Description.toLowerCase().includes('gift') || purchase.Description.toLowerCase().includes('cadeau'),
  );
  // Voir si on peut utiliser barcode (purchase sell item)

  return (
    <>
      {filteredPurchases && filteredPurchases.length > 0 ? (
        <Box display={'none'} className="accountBox" w="full" sx={{ padding: '1em' }}>
          <Heading textTransform="uppercase" className="accountSubTitle" size="md">
            Cartes cadeaux offertes
          </Heading>

          {isLoading ? (
            <Center mt={10}>
              <CircularProgress isIndeterminate color="primary.500" />
            </Center>
          ) : (
            <Stack>
              {filteredPurchases.length ? (
                filteredPurchases.slice(0, 5).map((purchase) => (
                  <HStack key={purchase.Sale.Id} borderBottom="1px solid black" py={3}>
                    <Text>{purchase.Description} - </Text>
                    <Text fontWeight="bold">{Math.abs(purchase.AmountPaid)}€</Text>
                  </HStack>
                ))
              ) : (
                <Text>Aucun achats</Text>
              )}
            </Stack>
          )}
        </Box>
      ) : null}
    </>
  );
}

interface PaymentMethodData {
  CreditCardNumber: string;
  BillingName: string;
  Exp: string;
  Cryptogram: string;
}

function PaymentMethod() {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const queryClient = useQueryClient();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PaymentMethodData>();
  const { t } = useTranslation();
  const { data: user, isLoading } = useFetchClientCompleteInfo();
  const hasPaymentMethodSetup = !!user?.Client.ClientCreditCard?.CardNumber;

  async function onSubmit(data: PaymentMethodData) {
    const sanitized: PaymentMethodData = {
      ...data,
      CreditCardNumber: data.CreditCardNumber.replace(/\D/g, ''),
      Cryptogram: data.Cryptogram.replace(/\D/g, ''),
    };

    try {
      const response = await HTTP.post<MindbodyUser>('/client/paymentinfo', sanitized).send();

      if (user) {
        const update = produce(user, (user) => {
          if (user) {
            user.Client.ClientCreditCard = response.ClientCreditCard;
          }
        });

        queryClient.setQueryData(['/client/completeinfo', user.Client.Id], update);
      }
      reset();
      onClose();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2400);
    } catch (error) {
      if (error instanceof ApiClientException) {
        setError('root', { message: parseMindbodyError(error.Message) });
        return null;
      }

      const { title, description } = resolveUserError('paymentMethod', error);
      setError('root', { message: description ? `${title}. ${description}` : title });
      return null;
    }
  }

  return (
    <UserProfileCard title={t('Méthode de paiement')}>
      <Skeleton isLoaded={!isLoading} mt={10}>
        <Box position="relative">
          <AnimatePresence>
            {showSuccess && (
              <MotionBox
                key="payment-success-overlay"
                position="absolute"
                inset={0}
                bg="white"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={2}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <VStack spacing={3}>
                  <MotionBox
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  >
                    <Box
                      w={16}
                      h={16}
                      borderRadius="full"
                      bg="green.100"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <CheckIcon w={8} h={8} color="green.600" />
                    </Box>
                  </MotionBox>
                  <MotionBox
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.25 }}
                  >
                    <Text fontWeight="semibold" color="green.700" textTransform="uppercase" letterSpacing="wide">
                      {t('Carte mise à jour')}
                    </Text>
                  </MotionBox>
                </VStack>
              </MotionBox>
            )}
          </AnimatePresence>

          {hasPaymentMethodSetup && !isOpen ? (
            <Stack>
              <HStack width="100%" justifyContent="space-between" alignItems="flex-start">
                <Text textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
                  {t('Nom')}
                </Text>
                <Text textAlign="right">{user.Client.ClientCreditCard?.CardHolder}</Text>
              </HStack>
              <HStack width="100%" justifyContent="space-between" alignItems="flex-start">
                <Text textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
                  {t('Numéro')}
                </Text>
                <Text textAlign="right" letterSpacing="wider">
                  {user.Client.ClientCreditCard?.CardNumber}
                </Text>
              </HStack>
              <HStack width="100%" justifyContent="space-between" alignItems="flex-start">
                <Text textTransform="uppercase" fontWeight="medium" className="accountFormLabel">
                  {t('Expire le')}
                </Text>
                <Text textAlign="right">
                  {user.Client.ClientCreditCard?.ExpMonth}/{user.Client.ClientCreditCard?.ExpYear}
                </Text>
              </HStack>

              <VStack padding="2em 0 0">
                <Button className="accountButton" variant="contained" onClick={onOpen}>
                  {t('Modifier')}
                </Button>
              </VStack>
            </Stack>
          ) : (
            <Stack as="form" onSubmit={handleSubmit(onSubmit)} mt={5}>
              <FormControl isInvalid={!!errors?.BillingName}>
                <FormLabel>{t('Nom du titulaire')}</FormLabel>
                <Input {...register('BillingName', { required: true })} />
              </FormControl>

              <Stack direction={{ base: 'column', md: 'row' }}>
                <FormControl isInvalid={!!errors?.CreditCardNumber}>
                  <FormLabel>{t('Numéro de carte')}</FormLabel>
                  <Input
                    {...register('CreditCardNumber', {
                      pattern: /\d\d\d\d \d\d\d\d \d\d\d\d \d\d\d\d/,
                      required: true,
                    })}
                    as={ReactInputMask}
                    mask="9999 9999 9999 9999"
                    maskChar={null}
                  />
                </FormControl>

                <HStack>
                  <FormControl isInvalid={!!errors?.Exp} minW={145}>
                    <FormLabel>{t("Date d'expiration")}</FormLabel>
                    <Input
                      {...register('Exp', {
                        pattern: /[0-1][0-9] \/ [0-9][0-9]/,
                        required: true,
                      })}
                      as={ReactInputMask}
                      mask="99 / 99"
                      maskChar={null}
                    />
                  </FormControl>
                  <FormControl isInvalid={!!errors?.Cryptogram}>
                    <FormLabel>{t('Cryptogramme')}</FormLabel>
                    <Input
                      {...register('Cryptogram', {
                        pattern: /[0-9][0-9][0-9]/,
                        required: true,
                      })}
                      as={ReactInputMask}
                      mask="9999"
                      maskChar={null}
                    />
                  </FormControl>
                </HStack>
              </Stack>
              <FormControl isInvalid={!!errors.root}>
                <FormErrorMessage>{errors.root?.message}</FormErrorMessage>
              </FormControl>
              <ButtonGroup mt={4} spacing={3} alignItems="center">
                <Button
                  type="submit"
                  className="accountButton"
                  variant="contained"
                  isDisabled={isSubmitting}
                  whiteSpace="nowrap"
                >
                  {isSubmitting ? (
                    <HStack spacing={2} align="center">
                      <Spinner size="sm" />
                      <Text as="span">{t('Enregistrement...')}</Text>
                    </HStack>
                  ) : (
                    t('Sauvegarder')
                  )}
                </Button>
                {hasPaymentMethodSetup && (
                  <Button
                    variant="link"
                    isDisabled={isSubmitting}
                    onClick={() => {
                      reset();
                      onClose();
                    }}
                    color="gray.600"
                    fontWeight="normal"
                    textDecoration="none"
                    _hover={{ textDecoration: 'underline', bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    _focus={{ boxShadow: 'none', bg: 'transparent' }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </ButtonGroup>
            </Stack>
          )}
        </Box>
      </Skeleton>
    </UserProfileCard>
  );
}

interface EditPasswordFormData {
  previousPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

function EditPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const formMethods = useForm<EditPasswordFormData>();

  if (mondayMindbody?.oauth?.enabled) {
    return null;
  }

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setError,
    reset,
  } = formMethods;

  async function onSubmit(data: EditPasswordFormData) {
    const { confirmNewPassword, newPassword, previousPassword } = data;
    if (newPassword !== confirmNewPassword) {
      return setError('confirmNewPassword', { message: t('Le mot de passe ne correspond pas') });
    }
    try {
      await HTTP.post('/auth/updatePassword', {
        previousPassword,
        newPassword,
      }).send();

      reset();

      toast({
        title: t('Votre mot de passe a bien été modifié.'),
        status: 'success',
      });
    } catch (e) {
      if (e instanceof ApiClientException) {
        if (e.ErrorCode === ApiClientExceptionErrorCode.InvalidLoginCredentials) {
          return setError('previousPassword', { message: 'Mot de passe invalide' });
        }

        setError('root', { message: e.getMessage() });
        return true;
      }

      const { title, description } = resolveUserError('passwordChange', e);
      setError('root', { message: description ? `${title}. ${description}` : title });
    }
    return true;
  }

  const registerOptions = {
    minLength: { value: 6, message: t('Le mot de passe doit faire au moins 6 charactères') },
    required: t('Champ obligatoire'),
  };
  return (
    <UserProfileCard title={t(`Modifier mon mot de passe`)}>
      <FormProvider {...formMethods}>
        <Stack as="form" onSubmit={handleSubmit(onSubmit)} mt={10} alignItems="flex-start">
          <FloatingLabelInput
            label={t('Ancien mot de passe')}
            type="password"
            name="previousPassword"
            errorMessage={errors?.previousPassword?.message}
            registerOptions={registerOptions}
          />

          <FloatingLabelInput
            label={t('Nouveau mot de passe')}
            type="password"
            name="newPassword"
            errorMessage={errors?.newPassword?.message}
            registerOptions={registerOptions}
            marginTop={4}
          />

          <FloatingLabelInput
            label={t('Confirmer le mot de passe')}
            type="password"
            name="confirmNewPassword"
            errorMessage={errors?.confirmNewPassword?.message}
            registerOptions={registerOptions}
            marginTop={4}
          />

          <FormControl isInvalid={!!errors.root}>
            <FormErrorMessage mb={5}>{errors.root?.message}</FormErrorMessage>
          </FormControl>

          <Button type="submit" isLoading={isSubmitting} isDisabled={!isValid}>
            Confirmer
          </Button>
        </Stack>
      </FormProvider>
    </UserProfileCard>
  );
}
function UserProfileCard({ title, children, ...props }: { title: string; children: React.ReactNode } & BoxProps) {
  return (
    <Box className="accountBox" w="full" sx={{ padding: '1em' }} {...props}>
      <Heading textTransform="uppercase" className="accountSubTitle" size="md">
        {title}
      </Heading>
      {children}
    </Box>
  );
}

export default UserProfile;
